/**
 * FeeCalculator — top-level shell for the Fee Calculator tool.
 *
 * Owns the mode switching (Calculator ↔ Decision Tree, plus Proposal
 * mode entered via the Calculator's "Generate Fee Proposal" button)
 * and injects the global styles ported from the source artifact:
 * Montserrat font, print rules, and number-input spinner reset.
 *
 * The Settings sub-tab from the original artifact is intentionally
 * omitted — fee-calculator settings live exclusively under the app's
 * Settings → Fee Calculator section (adviser-only).
 *
 * Both adviser and assistant see and use this component identically;
 * role gating happens at the route level.
 */

import React, { useEffect, useState } from "react";
import { C } from "./brand";
import Calculator from "./Calculator";
import DecisionTree from "./DecisionTree";
import Proposal from "./Proposal";
import { useFeeCalculatorSettings } from "../../../hooks/useFeeCalculatorSettings";

const FONT_LINK_ID = "opt-fonts";
const STYLE_TAG_ID = "opt-fee-calc-styles";

// Montserrat is loaded once on first mount. The CSS block also supplies
// the Tailwind-lookalike utility classes the ported calculator uses
// (Inbox doesn't run Tailwind), all scoped under `.opt-fee-calc` so
// nothing leaks into Inbox's own styling. Print rules additionally
// hide Inbox's AppShell Header (.header-bar) and Footer (<footer>) so
// the proposal prints as a standalone client document.
const PRINT_AND_FONT_CSS = `
  /* Layout utilities — scoped to the calculator only */
  .opt-fee-calc .flex { display: flex; }
  .opt-fee-calc .flex-col { flex-direction: column; }
  .opt-fee-calc .items-baseline { align-items: baseline; }
  .opt-fee-calc .items-center { align-items: center; }
  .opt-fee-calc .items-start { align-items: flex-start; }
  .opt-fee-calc .justify-between { justify-content: space-between; }
  .opt-fee-calc .justify-center { justify-content: center; }
  .opt-fee-calc .gap-2 { gap: 0.5rem; }
  .opt-fee-calc .gap-3 { gap: 0.75rem; }
  .opt-fee-calc .gap-6 { gap: 1.5rem; }
  .opt-fee-calc .gap-8 { gap: 2rem; }
  .opt-fee-calc .mb-4 { margin-bottom: 1rem; }
  .opt-fee-calc .mb-6 { margin-bottom: 1.5rem; }
  .opt-fee-calc .mt-6 { margin-top: 1.5rem; }
  .opt-fee-calc .grid { display: grid; }
  .opt-fee-calc .grid-cols-1 { grid-template-columns: minmax(0, 1fr); }
  .opt-fee-calc .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (min-width: 768px) {
    .opt-fee-calc .md\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }
  /* Number-input spinner reset (matches the source artifact) */
  .opt-fee-calc input[type="number"]::-webkit-outer-spin-button,
  .opt-fee-calc input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none; margin: 0;
  }
  .opt-fee-calc input[type="number"] { -moz-appearance: textfield; }
  /* Reset radio/checkbox inside the calculator. Inbox's globals.css
     applies a generic 'input { width: 100%; padding; border; ... }'
     rule (and a 44px min-height under @media pointer:coarse) which
     stretches radios/checkboxes to fill the option cards and pushes
     the label text to the far right. Restore native control sizing. */
  .opt-fee-calc input[type="radio"],
  .opt-fee-calc input[type="checkbox"] {
    width: auto;
    min-height: 0;
    padding: 0;
    border: none;
    background: transparent;
    border-radius: 0;
    box-shadow: none;
    flex-shrink: 0;
  }
  .opt-fee-calc input[type="radio"]:focus,
  .opt-fee-calc input[type="checkbox"]:focus {
    box-shadow: none;
    outline: 2px solid var(--cp-secondary);
    outline-offset: 1px;
  }
  /* Print: hide Inbox chrome and any element marked print:hidden */
  @media print {
    body { background: white !important; }
    .header-bar, footer { display: none !important; }
    .opt-fee-calc .print\\:hidden { display: none !important; }
    .opt-fee-calc .proposal-doc { box-shadow: none !important; border: none !important; padding: 0 !important; }
  }
`;

function ensureGlobalStyles() {
  if (typeof document === "undefined") return;
  if (!document.getElementById(FONT_LINK_ID)) {
    const link = document.createElement("link");
    link.id = FONT_LINK_ID;
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }
  if (!document.getElementById(STYLE_TAG_ID)) {
    const style = document.createElement("style");
    style.id = STYLE_TAG_ID;
    style.textContent = PRINT_AND_FONT_CSS;
    document.head.appendChild(style);
  }
}

export default function FeeCalculator() {
  const { settings, loaded } = useFeeCalculatorSettings();
  const [mode, setMode] = useState("calculator"); // calculator | tree | proposal
  const [proposalData, setProposalData] = useState(null);

  useEffect(() => {
    ensureGlobalStyles();
  }, []);

  const generateProposal = (data) => {
    setProposalData(data);
    setMode("proposal");
  };

  const tabs = [
    { id: "calculator", label: "Calculator" },
    { id: "tree", label: "Decision Tree" },
  ];

  return (
    <div
      className="opt-fee-calc"
      style={{
        fontFamily: "'Montserrat', 'Trebuchet MS', 'Segoe UI', Arial, sans-serif",
        color: C.text,
      }}
    >
      {/* Sub-nav (Calculator / Decision Tree). Hidden in proposal mode
          — the proposal has its own back-button. */}
      {mode !== "proposal" && (
        <nav
          className="print:hidden"
          role="tablist"
          style={{
            display: "flex",
            gap: 4,
            marginBottom: 24,
            borderBottom: `1px solid ${C.border}`,
            paddingBottom: 0,
          }}
        >
          {tabs.map((t) => {
            const active = mode === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setMode(t.id)}
                role="tab"
                aria-selected={active}
                style={{
                  background: "transparent",
                  color: active ? C.tealDark : C.muted,
                  border: "none",
                  borderBottom: `2px solid ${active ? C.tealDark : "transparent"}`,
                  padding: "10px 18px",
                  marginBottom: -1,
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                  letterSpacing: 0.3,
                  transition: "all 0.15s",
                }}
              >
                {t.label}
              </button>
            );
          })}
        </nav>
      )}

      {!loaded ? (
        <div style={{ padding: 80, textAlign: "center", color: C.muted, fontSize: 14 }}>Loading…</div>
      ) : mode === "calculator" ? (
        <Calculator settings={settings} onGenerateProposal={generateProposal} />
      ) : mode === "tree" ? (
        <DecisionTree settings={settings} />
      ) : mode === "proposal" ? (
        <Proposal data={proposalData} settings={settings} onBack={() => setMode("calculator")} />
      ) : null}
    </div>
  );
}
